import { useState } from 'react';
import { Loader2, Zap, ArrowLeft } from 'lucide-react';
import { adGenerationApi } from '@/services/api';
import { MultiSelectTypeahead } from '@/components/MultiSelectTypeahead';
import { VISUAL_STYLE_OPTIONS, CHANNEL_OPTIONS, SIZE_OPTIONS } from '@/constants/adPackOptions';

interface AdPackConfigPanelProps {
  clientUid: string;
  briefId?: string;
  onSubmit: (jobId: string) => void;
  onCancel: () => void;
}

export function AdPackConfigPanel({ clientUid, briefId, onSubmit, onCancel }: AdPackConfigPanelProps) {
  const [config, setConfig] = useState({
    total_ads: 10,
    top_products: 5,
    creative_strategy: 'auto',
    visual_styles: [] as string[],
    channels: [] as string[],
    sizes: [] as string[],
    fast_mode: false,
    enable_refinement: true,
    generate_landing_pages: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adGenerationApi.submitJob({
        client_uid: clientUid,
        brief_id: briefId,
        ...config,
      });
      onSubmit(result.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit job');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = <K extends keyof typeof config>(key: K, value: typeof config[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 border border-emerald-500/20">
              <Zap size={18} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy-900">Generate Ad Pack</h2>
              <p className="text-xs text-slate-500 mt-0.5">Configure and launch ad generation pipeline</p>
            </div>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1"
        >
          <ArrowLeft size={12} />
          Back
        </button>
      </div>

      {/* Context Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-[10px] text-slate-400 uppercase font-bold">Client</span>
            <div className="font-mono text-slate-700 mt-1">{clientUid}</div>
          </div>
          {briefId && (
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-bold">Brief ID</span>
              <div className="font-mono text-purple-500 mt-1">{briefId}</div>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Form */}
      <div className="space-y-5">
        {/* Total Ads */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">
            Total Ads
            <span className="text-xs text-slate-400 ml-2 font-normal">(1-50)</span>
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="1"
              max="50"
              value={config.total_ads}
              onChange={(e) => updateConfig('total_ads', parseInt(e.target.value))}
              className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <input
              type="number"
              min="1"
              max="50"
              value={config.total_ads}
              onChange={(e) => updateConfig('total_ads', parseInt(e.target.value) || 10)}
              className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Top Products */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">
            Top Products
            <span className="text-xs text-slate-400 ml-2 font-normal">(1-20)</span>
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="1"
              max="20"
              value={config.top_products}
              onChange={(e) => updateConfig('top_products', parseInt(e.target.value))}
              className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <input
              type="number"
              min="1"
              max="20"
              value={config.top_products}
              onChange={(e) => updateConfig('top_products', parseInt(e.target.value) || 5)}
              className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Creative Strategy */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">
            Creative Strategy
          </label>
          <select
            value={config.creative_strategy}
            onChange={(e) => updateConfig('creative_strategy', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
          >
            <option value="auto">Auto (Recommended)</option>
            <option value="product_showcase">Product Showcase</option>
            <option value="lifestyle_action">Lifestyle Action</option>
            <option value="problem_solution">Problem Solution</option>
            <option value="social_proof">Social Proof</option>
          </select>
        </div>

        {/* Creative Dimensions */}
        <div className="space-y-4 border-t border-slate-200 pt-4">
          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wide">Creative Dimensions</div>
          <MultiSelectTypeahead
            label="Visual Style"
            sublabel="creative execution style"
            options={VISUAL_STYLE_OPTIONS}
            selected={config.visual_styles}
            onChange={(v) => updateConfig('visual_styles', v)}
            placeholder="e.g. UGC, Testimonial..."
          />
          <MultiSelectTypeahead
            label="Channel"
            options={CHANNEL_OPTIONS}
            selected={config.channels}
            onChange={(v) => updateConfig('channels', v)}
            placeholder="e.g. Meta, TikTok..."
          />
          <MultiSelectTypeahead
            label="Size"
            sublabel="image dimensions"
            options={SIZE_OPTIONS}
            selected={config.sizes}
            onChange={(v) => updateConfig('sizes', v)}
            placeholder="e.g. 1080x1350..."
          />
        </div>

        {/* Toggles */}
        <div className="space-y-3 border-t border-slate-200 pt-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-sm font-medium text-slate-700">Fast Mode</div>
              <div className="text-xs text-slate-400">Faster generation with fewer iterations</div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={config.fast_mode}
                onChange={(e) => updateConfig('fast_mode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-500/20 rounded-full peer peer-checked:bg-emerald-500 transition-colors">
                <div className="absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-5"></div>
              </div>
            </div>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-sm font-medium text-slate-700">Enable Refinement</div>
              <div className="text-xs text-slate-400">Post-review refinement loop for higher quality</div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={config.enable_refinement}
                onChange={(e) => updateConfig('enable_refinement', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-500/20 rounded-full peer peer-checked:bg-emerald-500 transition-colors">
                <div className="absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-5"></div>
              </div>
            </div>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-sm font-medium text-slate-700">Generate Landing Pages</div>
              <div className="text-xs text-slate-400">Create landing page briefs for each ad</div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={config.generate_landing_pages}
                onChange={(e) => updateConfig('generate_landing_pages', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-500/20 rounded-full peer peer-checked:bg-emerald-500 transition-colors">
                <div className="absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-5"></div>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-emerald-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Starting Generation...
            </>
          ) : (
            <>
              <Zap size={14} />
              Start Generation
            </>
          )}
        </button>
      </div>

      {/* Info Footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-700">
          <strong>Note:</strong> Generation typically takes 5-15 minutes depending on configuration. You can monitor progress in the Ad Generation dashboard.
        </p>
      </div>
    </div>
  );
}
